/**
 * Replaceable parts: the types and the fields each type has. Each type has its OWN fields (a bulb needs a base,
 * a filter needs a size) so what is recorded stays consistent. "Other" uses up to four spec rows.
 */
export type PartTypeId = "bulb" | "filter" | "battery" | "remote" | "other";

export type PartField = {
  key: string;
  label: string;
  /** Tap choices. Typing is allowed when `free` is set. */
  options?: string[];
  free?: boolean;
  unit?: string;
  placeholder?: string;
};

export type PartTypeDef = { id: PartTypeId; label: string; plural: string; fields: PartField[] };

export const PART_TYPES: PartTypeDef[] = [
  {
    id: "bulb",
    label: "Bulb",
    plural: "Bulbs",
    fields: [
      { key: "base", label: "Base", options: ["E26", "E12", "GU10", "GU24"], free: true },
      { key: "shape", label: "Shape", options: ["A19", "BR30", "BR40", "PAR30", "Candle"], free: true },
      { key: "watts", label: "Watts", unit: "W", free: true, placeholder: "65" },
      { key: "color", label: "Color", options: ["2700K", "3000K", "4000K", "5000K"] },
      { key: "dimmable", label: "Dimmable", options: ["Yes", "No"] },
    ],
  },
  {
    id: "filter",
    label: "Filter",
    plural: "Filters",
    fields: [
      { key: "purpose", label: "For", options: ["Fridge water", "HVAC", "Range hood", "Humidifier"], free: true },
      { key: "size", label: "Size", free: true, placeholder: "16 × 25 × 1 in" },
    ],
  },
  {
    id: "battery",
    label: "Battery",
    plural: "Batteries",
    fields: [
      { key: "type", label: "Type", options: ["AA", "AAA", "9V", "CR2032", "C", "D"], free: true },
      { key: "device", label: "Powers", free: true, placeholder: "Smoke detector" },
    ],
  },
  {
    id: "remote",
    label: "Remote",
    plural: "Remotes",
    fields: [
      { key: "device", label: "Controls", free: true, placeholder: "TV" },
      { key: "battery", label: "Battery", options: ["AA", "AAA", "CR2032"], free: true },
    ],
  },
  { id: "other", label: "Other", plural: "Other", fields: [] },
];

export const SPEC_LABEL_SUGGESTIONS = ["Size", "Color", "Finish", "Material", "Thread", "Model number", "Voltage"];

export type PartSpec = Record<string, string> & { rows?: never };
export type OtherRow = { label: string; value: string };

export interface PropertyPart {
  id: string;
  property_id: string;
  part_type: string;
  custom_type_id: string | null;
  name: string;
  room: string | null;
  place: string | null;
  spec: Record<string, unknown>;
  quantity: number;
  brand: string | null;
  model: string | null;
  store: string | null;
  photo_label_url: string | null;
  photo_place_url: string | null;
  replace_every_months: number | null;
  last_replaced: string | null;
  cleaner_can_replace: boolean;
  keep_spares: number;
  appliance_id: string | null;
  spare_inventory_item_id: string | null;
  notes: string | null;
}

export function partTypeDef(id: string): PartTypeDef {
  return PART_TYPES.find((t) => t.id === id) ?? PART_TYPES[PART_TYPES.length - 1];
}

export function otherRows(spec: Record<string, unknown> | null | undefined): OtherRow[] {
  const rows = (spec as { rows?: unknown } | null)?.rows;
  return Array.isArray(rows)
    ? (rows as OtherRow[]).filter((r) => r && (r.label || r.value))
    : [];
}

/** "BR30 65W 2700K dimmable × 4" — the one-line spec summary used in lists and cards. */
export function partSummary(p: Pick<PropertyPart, "part_type" | "spec" | "quantity">): string {
  const s = (p.spec ?? {}) as Record<string, string>;
  const qty = p.quantity > 1 ? ` × ${p.quantity}` : "";
  switch (p.part_type) {
    case "bulb": {
      const bits = [s.shape, s.watts ? `${s.watts}W` : "", s.color, s.dimmable === "Yes" ? "dimmable" : ""].filter(Boolean);
      return (bits.length ? bits.join(" ") : s.base || "Bulb") + qty;
    }
    case "filter":
      return [s.purpose, s.size].filter(Boolean).join(" · ") || "Filter";
    case "battery":
      return [s.type ? `${s.type}${qty}` : "", s.device].filter(Boolean).join(" · ") || "Battery";
    case "remote":
      return [s.device, s.battery ? `${p.quantity > 1 ? p.quantity + " × " : ""}${s.battery}` : ""].filter(Boolean).join(" · ") || "Remote";
    default: {
      const rows = otherRows(p.spec);
      return rows.map((r) => `${r.label ? r.label + " " : ""}${r.value}`.trim()).join(" · ") || "Part";
    }
  }
}

/** The name a spare supply line gets: "BR30 65W 2700K bulb". */
export function spareName(p: Pick<PropertyPart, "part_type" | "spec" | "name" | "quantity">): string {
  const sum = partSummary({ ...p, quantity: 1 });
  const word = partTypeDef(p.part_type).label.toLowerCase();
  return p.part_type === "other" ? p.name : p.part_type === "battery" ? `${sum} batteries` : `${sum} ${word}`.trim();
}

export function replaceEveryLabel(months: number | null): string | null {
  if (!months) return null;
  if (months % 12 === 0) return months === 12 ? "every year" : `every ${months / 12} yr`;
  return `every ${months} mo`;
}

/** Store search link built from brand + model (no stored link, so it never goes stale). */
export function storeSearchUrl(p: Pick<PropertyPart, "brand" | "model" | "name" | "store">): string {
  const q = [p.brand, p.model || p.name].filter(Boolean).join(" ");
  const store = (p.store ?? "").toLowerCase();
  const enc = encodeURIComponent(q);
  if (store.includes("home depot")) return `https://www.homedepot.com/s/${enc}`;
  if (store.includes("amazon")) return `https://www.amazon.com/s?k=${enc}`;
  if (store.includes("lowe")) return `https://www.lowes.com/search?searchTerm=${enc}`;
  if (store.includes("walmart")) return `https://www.walmart.com/search?q=${enc}`;
  return `https://www.google.com/search?tbm=shop&q=${enc}`;
}

/** A part being captured in the onboarding camera loop (becomes a property_parts row on import). */
export type PartDraft = {
  type: PartTypeId;
  spec: Record<string, string>;
  rows: OtherRow[];
  place: string;
  brand: string;
  store: string;
  canReplace: boolean;
  keepSpares: number;
  replaceMonths: number | null;
};

export const emptyPartDraft = (): PartDraft => ({
  type: "bulb",
  spec: {},
  rows: [{ label: "", value: "" }],
  place: "",
  brand: "",
  store: "",
  canReplace: false,
  keepSpares: 1,
  replaceMonths: null,
});
